import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useEffect, useCallback} from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import MultiAnalysisTemplateSelector from 'analysis-tool/templates/MultiAnalysisTemplateSelector';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Section} from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelector from 'file-browser/FolderSelector';
import {BioentitySummary, SchemaTag} from 'folder-items/FileConstants';
import HelpButton from 'help/HelpButton';
import {isFileProcessingTemplate} from 'instruments/analysis-templates/AnalysisTemplatesEditableField';
import {ConnectionSchema} from 'instruments/connection-schemas/ConnectionSchemaConstants';
import getCustomTagEditors from 'instruments/connections/ConnectionTagCustomEditors';
import EquipmentSearchInput from 'instruments/equipment/EquipmentSearchInput';
import {InstrumentGateway} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import SchemaTagInputs from 'schemas/tags/SchemaTagInputs';
import {forceRefreshSearch} from 'search/SearchUIReduxActions';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Link from 'ui-library/Link';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {listGateways} from '../instrument-gateways/InstrumentGatewayDAO';
import {convertToSchemaTag, getConnectionTagsToSet} from './connection-editor/ConnectionEditorUtils';
import {ConnectionFieldType} from './ConnectionConstants';
import {createConnection, getAdapter, listAdapters} from './ConnectionDAO';

interface CreateConnectionModalProps {
  onRequestClose: () => void;
  connectionSchema: ConnectionSchema;
}

export const emptyGatewayOptionLabel = 'Select gateway';

function modalTitle(name: string): string {
  return `Create new ${name} connection`;
}

export default function CreateConnectionModal(props: CreateConnectionModalProps): JSX.Element {
  const [instrumentGatewaysById, setInstrumentGatewaysById] = useState(
    null as {[id: string]: InstrumentGateway} | null
  );
  const [name, setName] = useState('');
  const [gatewayId, setGatewayId] = useState<string | null>(null);
  const [dataCatalogFolderId, setDataCatalogFolderId] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState(false);
  const [gatewayTags, setGatewayTags] = useState([] as Array<SchemaTag>);
  const [tags, setTags] = useState([] as Array<SchemaTag>);
  const [fieldInfos, setFieldInfos] = useState({} as {[id: string]: string | null});
  const [fieldTypes, setFieldTypes] = useState({} as {[id: string]: string | null});
  const [equipment, setEquipment] = useState<Array<BioentitySummary>>([]);
  const [analysisTemplates, setAnalysisTemplates] = useState<Array<AnalysisTemplateSelectorTemplate>>([]);

  const handleAnalysisTemplateChange = useCallback((templates: Array<AnalysisTemplateSelectorTemplate>) => {
    setAnalysisTemplates(templates);
  }, []);

  const handleSubmit = async (): Promise<void> => {
    assert(canSubmit, 'Cannot call submission handler when state is invalid for submission');
    setIsPosting(true);
    try {
      const {id: connectionId} = await withLoadingScreen(
        createConnection({
          schemaId: props.connectionSchema.id,
          name,
          gatewayId,
          gatewayType: instrumentGatewaysById![gatewayId].gatewayType,
          dataCatalogFolderId,
          tags: getConnectionTagsToSet(tags),
          equipmentIds: equipment.map((item) => item.id),
          analysisTemplateIds: analysisTemplates.map((template) => template.id),
        }),
        'Creating connection...'
      );
      FileBrowserActions.openFile(connectionId);
      getStore().dispatch(forceRefreshSearch(Section.CONNECTIONS));
      props.onRequestClose();
    } catch (err) {
      setIsPosting(false);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  };

  const handleUpdateGatewayId = (updatedGatewayId: string | null): void => {
    setGatewayId(updatedGatewayId);
  };

  useOnMount(() => {
    listAdapters(props.connectionSchema.integrationAdapterId).then(({instrumentAdapters}) => {
      const supportedGatewayTypes = new Set(
        instrumentAdapters.map((instrumentAdapter) => instrumentAdapter.gatewayType)
      );
      listGateways().then(({gateways}) => {
        const availableGateways = gateways.filter(
          (gateway) => !gateway.isArchived && supportedGatewayTypes.has(gateway.gatewayType)
        );
        setInstrumentGatewaysById(
          objectFromEntries(availableGateways.map((gateway) => [gateway.id, gateway]))
        );
      });
    });
  });

  useEffect(() => {
    if (instrumentGatewaysById && gatewayId) {
      setTags([]);
      setFieldInfos({});
      setFieldTypes({});
      getAdapter(
        props.connectionSchema.integrationAdapterId,
        instrumentGatewaysById[gatewayId].gatewayType
      ).then((adapter) => {
        setTags(
          adapter.fields
            .filter(
              (field) =>
                field.fieldType === ConnectionFieldType.TEXT ||
                field.fieldType === ConnectionFieldType.SECRET_REF
            )
            .map((field) => convertToSchemaTag(field))
        );
        setGatewayTags(
          adapter.fields
            .filter((field) => field.fieldType === ConnectionFieldType.GATEWAY)
            .map((field) =>
              convertToSchemaTag(field, undefined, (instrumentGatewaysById[gatewayId].fields || {})[field.id])
            )
        );
        setFieldInfos(Object.fromEntries(adapter.fields.map((field) => [field.id, field.info])));
        setFieldTypes(
          Object.fromEntries(
            adapter.fields.map((field) => [field.id, field.fieldType ? field.fieldType : null])
          )
        );
      });
    }
  }, [gatewayId, props.connectionSchema.integrationAdapterId, instrumentGatewaysById]);

  if (instrumentGatewaysById == null) {
    return (
      <Modal title={modalTitle(props.connectionSchema.name)} onRequestClose={props.onRequestClose}>
        <CenteredHorizontalThrobber />
      </Modal>
    );
  }

  const hasRequiredFolderId = dataCatalogFolderId != null;

  const canSubmit = gatewayId != null && hasRequiredFolderId && name.trim().length !== 0 && !isPosting;
  return (
    <Modal
      title={modalTitle(props.connectionSchema.name)}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          text: isPosting ? 'Creating...' : 'Create',
          submit: true,
          disabled: !canSubmit,
        },
      ]}
      analyticsModule={ANALYTICS_MODULE.CONNECT}
      analyticsModalType="create_connection"
      analyticsMetadata={{connectionSchemaId: props.connectionSchema.id}}
    >
      <div className={css(styles.wrapper)}>
        <FormGroup>
          <Row>
            <Col sm={6}>
              <TextField
                label="Connection Name"
                labelStyle={LabelStyle.LABEL}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="Name"
                value={name}
                isRequired={true}
              />
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Row>
            <Col sm={6}>
              <FieldLabel isRequired={true} label="Benchling Gateway" labelStyle={LabelStyle.LABEL} />
              <SelectDropdown
                menu={
                  <SearchableMenu
                    selectedValue={gatewayId}
                    optionDatas={[
                      {name: emptyGatewayOptionLabel, value: null},
                      ...Object.values(instrumentGatewaysById).map((option) => ({
                        name: option.name,
                        value: option.id,
                      })),
                    ]}
                    onSelect={(gatewayId: string | null) => {
                      handleUpdateGatewayId(gatewayId);
                    }}
                    height={Menu.HEIGHTS.MEDIUM}
                    width={Menu.WIDTHS.FULL}
                    scrollable={true}
                  />
                }
                displayName={(gatewayId && instrumentGatewaysById[gatewayId]?.name) ?? 'Select gateway'}
              />
            </Col>
            {gatewayId && (
              <Col sm={6}>
                <FieldLabel isRequired={true} label="Benchling Location" labelStyle={LabelStyle.LABEL} />
                <HelpButton
                  renderContent={() =>
                    'Files will be stored and can be accessed through this folder inside of Benchling.'
                  }
                  iconClassName="u-mini-spacing-left"
                />
                <FolderSelector
                  value={dataCatalogFolderId}
                  onChange={(e: {target: {value: string}}) => {
                    setDataCatalogFolderId(e.target.value);
                  }}
                  unsetOnFetchFail={true}
                />
              </Col>
            )}
          </Row>
        </FormGroup>
        {gatewayId ? (
          <>
            {tags.length > 0 ? (
              <>
                <FormGroup>
                  <SchemaTagInputs
                    labelStyle={LabelStyle.LABEL}
                    itemLinkAllowed={false}
                    onUpdateTags={setTags}
                    tags={gatewayTags}
                    tagPopovers={fieldInfos}
                    customEditors={getCustomTagEditors(fieldTypes)}
                    isWritable={false}
                  />
                </FormGroup>
                <FormGroup>
                  <SchemaTagInputs
                    labelStyle={LabelStyle.LABEL}
                    itemLinkAllowed={false}
                    onUpdateTags={setTags}
                    tags={tags}
                    tagPopovers={fieldInfos}
                    customEditors={getCustomTagEditors(fieldTypes)}
                    isCreating={true}
                  />
                </FormGroup>
                {(BenchlingConfig.ENABLE_EQUIPMENT ||
                  (BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES &&
                    BenchlingConfig.PIPELINES_ENABLE_CONNECTION_INTEGRATION)) && (
                  <FormGroup>
                    <Row>
                      {BenchlingConfig.ENABLE_EQUIPMENT && (
                        <Col sm={6}>
                          <FieldLabel
                            isRequired={false}
                            label="Associated Equipment"
                            labelStyle={LabelStyle.LABEL}
                          />
                          <EquipmentSearchInput
                            shouldAllowMultiple={true}
                            onChange={(equipment) => {
                              setEquipment(equipment as Array<BioentitySummary>);
                            }}
                            selectedItems={equipment}
                            autoFocus={false}
                          />
                        </Col>
                      )}
                      {BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES &&
                        BenchlingConfig.PIPELINES_ENABLE_CONNECTION_INTEGRATION && (
                          <Col sm={6}>
                            <FieldLabel
                              isRequired={false}
                              label="Analysis Templates"
                              labelStyle={LabelStyle.LABEL}
                            />
                            <MultiAnalysisTemplateSelector
                              filterTemplates={isFileProcessingTemplate}
                              selectedItems={analysisTemplates}
                              onChange={handleAnalysisTemplateChange}
                              shouldAllowMultiple={true}
                              hideSelectedResults={true}
                            />
                          </Col>
                        )}
                    </Row>
                  </FormGroup>
                )}
              </>
            ) : (
              <CenteredHorizontalThrobber />
            )}
          </>
        ) : null}
        {gatewayId ? (
          <FormGroup>
            <Link
              href="https://help.benchling.com/hc/en-us/articles/22558210727565-Instruments-supported-by-Benchling-Connect"
              target="_blank"
            >
              Benchling Instrument Configuration Guides
            </Link>
          </FormGroup>
        ) : null}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
});
